<template>
  <div ref="root" class="point-sources-menu rel">
    <button
      ref="trigger"
      type="button"
      class="button button--plain button--h-25 bg--white color--black font-weight--500 mr--15"
      :class="{ disabled: !canAttach }"
      :title="canAttach ? t('popup.content.sources.title') : t('popup.content.sources.unavailable')"
      :aria-label="t('popup.content.sources.title')"
      :aria-disabled="canAttach ? undefined : 'true'"
      :aria-expanded="open ? 'true' : 'false'"
      aria-haspopup="dialog"
      @click="toggle"
    >
      <i class="fas" :class="uploading ? 'fa-spinner fa-spin' : 'fa-paperclip'" aria-hidden="true"></i>
      <span v-if="documents.length" class="point-sources-menu__count">{{ documents.length }}</span>
    </button>
    <div
      v-if="open"
      class="point-sources-menu__popover"
      role="dialog"
      :aria-label="t('popup.content.sources.title')"
      @keydown.esc="close"
    >
      <p class="point-sources-menu__title">{{ t('popup.content.sources.title') }}</p>
      <source-document-list
        variant="bar"
        :documents="documents"
        :uploading="uploading"
        :error="error"
        @upload="upload"
        @attach-url="attachUrl"
        @remove="remove"
      ></source-document-list>
    </div>
  </div>
</template>

<script setup lang="ts">
// The Quick Edit Bar's way to a point's source documents: a paperclip counting
// them, opening a popover to add or delete one. Documents are stored as soon as
// they are added, so nothing here takes part in the bar's unsaved-content check.
// Until an LLM is connected the paperclip is disabled and says why; the
// `disabled` class and `aria-disabled`, not `disabled`, keep its tooltip
// showing.

import { onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import SourceDocumentList from './SourceDocumentList.vue';
import { usePointSourceDocuments } from '../composables/usePointSourceDocuments';
import type { StatusSocket } from '../composables/usePointSourceDocuments';

const props = withDefaults(
  defineProps<{
    mapId: string | number | null;
    nodeId: number | null;
    /** Whether documents can be used: an LLM is connected. */
    canAttach?: boolean;
  }>(),
  { canAttach: true }
);

const { t } = useI18n();
const store = useStore();

const { documents, uploading, error, upload, attachUrl, remove } = usePointSourceDocuments({
  mapId: () => props.mapId,
  nodeId: () => props.nodeId,
  getSocket: () =>
    (store.getters['simultaneousEditing/getSocket'] as StatusSocket | null) ?? null,
});

const open = ref(false);
const root = ref<HTMLElement | null>(null);
const trigger = ref<HTMLButtonElement | null>(null);

// `pointerdown`, not `click`: a click inside re-renders the popover's row before
// a document-level click listener runs, so the target would already be detached
// and read as outside.
function onPointerDown(event: PointerEvent): void {
  if (!root.value?.contains(event.target as Node)) open.value = false;
}

function toggle(): void {
  if (!props.canAttach) return;
  open.value = !open.value;
}

function close(): void {
  open.value = false;
  trigger.value?.focus();
}

watch(open, isOpen => {
  if (isOpen) document.addEventListener('pointerdown', onPointerDown);
  else document.removeEventListener('pointerdown', onPointerDown);
});
// Another point selected: its documents are not what the popover was opened for.
watch(
  () => [props.nodeId, props.canAttach],
  () => {
    open.value = false;
  }
);
onBeforeUnmount(() => document.removeEventListener('pointerdown', onPointerDown));
</script>

<style scoped lang="scss">
@use "@/scss/local" as *;

.point-sources-menu .disabled {
  opacity: 0.5;
}

.point-sources-menu__count {
  margin-left: 6px;
}

.point-sources-menu__popover {
  position: absolute;
  z-index: 1;
  right: 0;
  bottom: 100%;
  width: 340px;
  margin-bottom: 8px;
  padding: 12px;
  border-radius: 2px;
  background: rgba($color-gray-base, 0.9);
  color: $white;
  text-align: left;
}

.point-sources-menu__title {
  margin-bottom: 10px;
  font-weight: 600;
}
</style>
